import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Loader2, Package2, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import ledgerViewsApi from '../../../api/ledgerViewsApi';
import formatCurrency from '../../../utils/formatCurrency';
import { parseDatePreservingCalendar } from '../../../utils/formatDate';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';
import {
    AccountingLedgerSource,
    ApLedgerRecord,
    ApRecurringEvidenceOverrideRequest,
    ApRecurringOverrideRequest,
    ApVendorDetailResponse,
    ApVendorRecord,
    ArCustomerDetailResponse,
    ArCustomerRecord,
    ArInvoiceRecord,
    ArManualCashAllocationRecord,
    ArManualCashBindingRecord,
    ArManualEvidenceExclusionRecord,
    ArManualLinkCandidate,
    ArLedgerDetailResponse,
    ArLedgerRecord,
    QboBillRecord,
    QboGeneralLedgerRecord,
    QboInvoiceRecord,
    QboLineItem,
    QboTransactionListItem,
    StandardAccount,
    UploadedLedgerRecord,
    VendorLineItemObservationRecord,
    VendorLineItemRecord
} from '../../../api/types';
import { getDocumentVendorName } from '../../../utils/getDocumentVendorName';
import { DrawerBadge, DrawerField } from '../shared/LedgerDrawerPrimitives';
import { buildInventoryDrawerPresentation } from '../inventory/InventoryDrawer';
import { buildApLedgerDrawerPresentation, buildApVendorDrawerPresentation } from '../ap/APRecordDrawer';
import StandardAccountSelector from './StandardAccountSelector';
import {
    AccountingLineItemsSection,
    buildGeneralLedgerDrawerPresentation,
    buildQboBillDrawerPresentation,
    buildQboInvoiceDrawerPresentation,
    buildTransactionListDrawerPresentation,
    buildUploadedDrawerPresentation,
    UploadedPriceSignalsSection,
} from '../accounting/AccountingRecordDrawer';
import { buildArCustomerDrawerPresentation, buildArLedgerDrawerPresentation } from '../ar/ARRecordDrawer';

type LedgerDatasetItem = ApLedgerRecord | ApVendorRecord | QboTransactionListItem | QboGeneralLedgerRecord | QboInvoiceRecord | QboBillRecord | ArCustomerRecord | ArLedgerRecord | UploadedLedgerRecord | VendorLineItemRecord;
type ArCustomerDetailInvoice = NonNullable<ArCustomerDetailResponse['invoices']>[number];
type ArCustomerDetailPayment = NonNullable<ArCustomerDetailResponse['qbo_payments']>[number];
type ArCustomerDetailBankReceipt = NonNullable<NonNullable<ArCustomerDetailResponse['bank_receipt_coverage']>['linked_bank_receipts']>[number];
type ArCustomerDetailCashApplication = NonNullable<ArCustomerDetailResponse['cash_applications']>[number];
type ArLedgerDetailPayment = NonNullable<ArLedgerDetailResponse['linked_payments']>[number];
type ArLedgerDetailBankReceipt = NonNullable<ArLedgerDetailResponse['linked_bank_receipts']>[number];
type ApRecurringStream = NonNullable<ApVendorDetailResponse['recurring_streams']>[number];
type ApRecurringBankEvidence = NonNullable<ApRecurringStream['bank_evidence_rows']>[number];
type ApRecurringFormState = {
    mode: 'add' | 'edit';
    targetStreamId?: string;
    matchVendorFamilyKey?: string;
    matchAccountingAccountName?: string;
    matchAccountingCategory?: string;
    matchFrequency?: string;
    vendorName: string;
    accountingAccountName: string;
    accountingCategory: string;
    frequency: string;
    typicalAmount: string;
    nextExpectedPaymentDate: Date | null;
    suggestedAmounts: number[];
};

const buildLegacyOverrideShape = (
    manualBinding?: ArManualCashBindingRecord | null,
    manualAllocations?: ArManualCashAllocationRecord[] | null,
    manualExclusions?: ArManualEvidenceExclusionRecord[] | null,
    fallbackOverride?: Record<string, any> | null,
) => {
    if (!manualBinding && !(manualAllocations || []).length && !(manualExclusions || []).length) {
        return fallbackOverride || null;
    }
    const nextOverride: Record<string, any> = {
        ...(fallbackOverride || {}),
        business_id: manualBinding?.business_id ?? fallbackOverride?.business_id ?? null,
        ledger_row_id: manualBinding?.ledger_row_id ?? fallbackOverride?.ledger_row_id ?? null,
        customer_id: manualBinding?.customer_id ?? fallbackOverride?.customer_id ?? null,
        customer_name: manualBinding?.customer_name ?? fallbackOverride?.customer_name ?? null,
        qbo_transaction_type_id: manualBinding?.qbo_transaction_type_id ?? fallbackOverride?.qbo_transaction_type_id ?? null,
        qbo_transaction_list_id: manualBinding?.qbo_transaction_list_id ?? fallbackOverride?.qbo_transaction_list_id ?? null,
        bank_transaction_id: manualBinding?.bank_transaction_id ?? fallbackOverride?.bank_transaction_id ?? null,
        linked_invoice_id: manualBinding?.selected_invoice_id ?? fallbackOverride?.linked_invoice_id ?? null,
        note: manualBinding?.note ?? fallbackOverride?.note ?? null,
        selection_source: manualBinding?.selection_source ?? fallbackOverride?.selection_source ?? null,
        actor_user_id: manualBinding?.actor_user_id ?? fallbackOverride?.actor_user_id ?? null,
        status: manualBinding?.status ?? fallbackOverride?.status ?? null,
        manual_allocations: (manualAllocations || []).map((item) => ({
            invoice_id: item.invoice_id ?? null,
            invoice_number: item.invoice_number ?? null,
            customer_id: item.customer_id ?? null,
            customer_name: item.customer_name ?? null,
            amount: item.allocated_amount ?? null,
            open_amount_snapshot: item.open_amount_snapshot ?? null,
        })),
        manual_allocation_total: (manualAllocations || []).reduce((sum, item) => sum + Number(item.allocated_amount || 0), 0),
        linked_evidence_exclusions: (manualExclusions || []).map((item) => ({
            target_object_type: item.target_object_type ?? null,
            target_source_id: item.target_source_id ?? null,
            excluded_field: item.excluded_field ?? null,
            linked_source_kind: item.linked_source_kind ?? null,
            linked_source_id: item.linked_source_id ?? null,
        })),
    };
    return nextOverride;
};
type ArCustomerInvoiceLinkRow = {
    invoice: ArCustomerDetailInvoice;
    invoiceId: string;
    invoiceNumber: string;
    matchedPayments: ArCustomerDetailPayment[];
    bankReceipts: ArCustomerDetailBankReceipt[];
    applications: ArCustomerDetailCashApplication[];
    allocatedTotal: number;
    evidenceTotal: number;
};

interface LedgerRecordDrawerProps {
    open: boolean;
    source: AccountingLedgerSource | 'inventory';
    record: LedgerDatasetItem | null;
    businessId?: string;
    onClose: () => void;
    onReviewDocument?: (record: UploadedLedgerRecord) => void;
    onOpenLinkedRecord?: (invoiceId: string, preferredSource?: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills' | null) => void;
    onOpenDocumentFile?: (fileId: string, source: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills') => void;
    onOpenArInvoice?: (invoiceId: string) => void;
    onOpenArCustomer?: (customerId: string, customerName?: string) => void;
    onOpenApBill?: (billId: string) => void;
    onOpenQboTransaction?: (transactionId: string) => void;
    onOpenCashTransaction?: (transactionId: string) => void;
    onOpenArPayment?: (paymentId: string) => void;
}

const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = parseDatePreservingCalendar(value);
    if (!parsed) return '—';
    return parsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatPercent = (value?: number | null, decimals = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return `${(value * 100).toFixed(decimals)}%`;
};

const formatTitleCase = (value?: string | null) => {
    const normalized = String(value || '').trim();
    if (!normalized) return '—';
    return normalized
        .split('_')
        .filter(Boolean)
        .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
        .join(' ');
};

const formatConfidencePercent = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    const normalized = value > 1 ? value / 100 : value;
    return `${(normalized * 100).toFixed(0)}%`;
};

const formatTransactionAmount = (
    amount?: number,
    currency?: string,
    amountMissing?: boolean,
    amountDerived?: boolean,
) => {
    if (amountMissing && !amountDerived) return '—';
    return formatCurrency(amount, currency || 'USD');
};

const clampMoney = (value: number) => {
    if (!Number.isFinite(value)) return 0;
    return Math.max(Math.round(value * 100) / 100, 0);
};

const isStaleArLedgerRowError = (error: unknown) => {
    const status = Number((error as { response?: { status?: number } } | undefined)?.response?.status || 0);
    return status === 400 || status === 404;
};

const getObservationDisplayDate = (observation?: VendorLineItemObservationRecord) =>
    observation?.invoice_date || observation?.effective_at || observation?.seen_at || '';

const getObservationSortTime = (value?: string | null) => {
    if (!value) return 0;
    const parsed = parseDatePreservingCalendar(value);
    return parsed ? parsed.getTime() : 0;
};

const getArInvoiceSortTime = (invoice?: {
    invoice_date?: string | null;
    event_date?: string | null;
    due_date?: string | null;
}) => (
    getObservationSortTime(invoice?.invoice_date || invoice?.event_date || invoice?.due_date)
);

const getLineItemQuantity = (lineItem?: QboLineItem) => {
    if (!lineItem?.quantity) return 1;
    if (typeof lineItem.quantity === 'number') return lineItem.quantity;
    return Number(lineItem.quantity.$numberLong || 1);
};

const compactStatusTone = (status?: string | null) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (['paid', 'collected', 'reconciled', 'matched', 'confirmed'].includes(normalized)) {
        return 'bg-emerald-100 text-emerald-800';
    }
    if (['credited'].includes(normalized)) {
        return 'bg-sky-100 text-sky-800';
    }
    if (['partial', 'partial_collection', 'partial_payment', 'detected'].includes(normalized)) {
        return 'bg-amber-100 text-amber-800';
    }
    if (['open', 'unreconciled', 'unapplied', 'revenue_candidate'].includes(normalized)) {
        return 'bg-slate-100 text-slate-700';
    }
    return 'bg-slate-100 text-slate-700';
};

const getFriendlyPaymentLabel = (payment: ArCustomerDetailPayment | ArLedgerDetailPayment | undefined, customerName?: string | null) => {
    const dateLabel = formatDate(payment?.payment_date || undefined);
    const amountLabel = formatCurrency(payment?.payment_amount, 'USD');
    const baseName = String(customerName || '').trim() || 'Customer';
    if (dateLabel !== '—') {
        return `${baseName} payment ${dateLabel}`;
    }
    return `${baseName} payment ${amountLabel}`;
};

const getFriendlyBankReceiptLabel = (
    receipt: ArCustomerDetailBankReceipt | ArLedgerDetailBankReceipt | undefined,
    customerName?: string | null,
) => {
    const counterparty = String(receipt?.counterparty_name || '').trim();
    if (counterparty) return counterparty;
    const baseName = String(customerName || '').trim() || 'Customer';
    const dateLabel = formatDate(receipt?.receipt_date || undefined);
    return dateLabel !== '—' ? `${baseName} bank deposit ${dateLabel}` : `${baseName} bank deposit`;
};

const getCreditMemoSummary = (
    payment: ArCustomerDetailPayment | ArLedgerDetailPayment | undefined,
) => {
    const numbers = (payment?.linked_credit_memo_numbers || []).filter(Boolean);
    if (numbers.length) return `Credit memo${numbers.length === 1 ? '' : 's'} ${numbers.join(', ')}`;
    const ids = (payment?.linked_credit_memo_ids || []).filter(Boolean);
    if (ids.length) return `Credit memo${ids.length === 1 ? '' : 's'} linked`;
    return '';
};

const getPaymentSourceSummary = (hasQboPayment: boolean, hasBankReceipt: boolean) => {
    if (hasQboPayment && hasBankReceipt) return 'QBO + bank matched';
    if (hasQboPayment) return 'QBO only';
    if (hasBankReceipt) return 'Bank only';
    return 'No evidence linked';
};

const isNonBankLinkedPayment = (
    linkedPaymentId: string | null | undefined,
    linkedPayments: Array<ArCustomerDetailPayment | ArLedgerDetailPayment>,
) => {
    const normalizedLinkedPaymentId = String(linkedPaymentId || '').trim();
    if (!normalizedLinkedPaymentId) return false;
    const matchedPayment = linkedPayments.find(
        (payment) => String(payment.source_id || '').trim() === normalizedLinkedPaymentId
    );
    if (!matchedPayment) return true;
    return String((matchedPayment as Record<string, unknown>).source_system || '').trim().toLowerCase() !== 'bank';
};

const getFriendlyBankReceiptStatus = (receipt: ArCustomerDetailBankReceipt | ArLedgerDetailBankReceipt | undefined) => {
    const normalizedStatus = String(receipt?.status || '').trim().toLowerCase();
    const hasLinkedPayment = !!String(receipt?.linked_payment_id || '').trim();
    if (normalizedStatus === 'partial_match' && !hasLinkedPayment) {
        return 'Suggested Match';
    }
    return formatTitleCase(receipt?.status);
};

const getApBillStatus = (bill?: QboBillRecord | null) => {
    const balanceDue = Number(bill?.data.balance_due || 0);
    if (balanceDue <= 0) return 'paid';
    const dueDate = parseDatePreservingCalendar(bill?.data.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dueDate) {
        const compare = new Date(dueDate);
        compare.setHours(0, 0, 0, 0);
        if (compare < today) return 'overdue';
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        if (compare <= weekEnd) return 'due this week';
    }
    return 'open';
};

const getArManualCandidateTypeLabel = (candidate?: ArManualLinkCandidate) => {
    const transactionType = String(candidate?.transaction_type || '').trim().toLowerCase();
    if (transactionType === 'payment') return 'QBO payment';
    if (transactionType === 'creditmemo' || transactionType === 'credit_memo') return 'Credit memo';
    if (transactionType === 'deposit') return 'QBO deposit';
    if (transactionType === 'invoice') return 'Invoice';
    if (candidate?.kind === 'bank_transaction' && isLowConfidenceGenericArBankCandidate(candidate)) {
        return 'Low confidence generic bank transaction';
    }
    if (candidate?.kind === 'bank_transaction') return 'Bank transaction';
    if (candidate?.kind === 'qbo_transaction') return 'QBO transaction';
    return 'AR candidate';
};

const getArManualCandidateTitle = (candidate?: ArManualLinkCandidate) => {
    const name = String(candidate?.counterparty_name || '').trim();
    const memo = String(candidate?.memo || '').trim();
    if (name) return name;
    if (memo) return memo;
    return getArManualCandidateTypeLabel(candidate);
};

const getArManualCandidateDeltaLabel = (candidate?: ArManualLinkCandidate) => {
    const delta = Number(candidate?.amount_delta);
    if (!Number.isFinite(delta)) return '';
    if (Math.abs(delta) < 0.005) return 'Exact amount';
    return `Delta ${formatCurrency(delta, candidate?.currency || 'USD')}`;
};

const isLowConfidenceGenericArBankCandidate = (candidate?: ArManualLinkCandidate) => {
    if (!candidate || candidate.kind !== 'bank_transaction') return false;
    if (candidate.candidate_classification === 'low_confidence_generic_bank_transaction') return true;
    const confidence = Number(candidate.match_confidence);
    const normalizedStatus = String(candidate.status || '').trim().toLowerCase();
    return Boolean(candidate.is_generic_counterparty) && (
        (!Number.isFinite(confidence) || confidence < 0.75)
        || normalizedStatus === 'unclassified'
        || normalizedStatus === 'unresolved'
    );
};

const getArManualBankCandidateGuidance = (candidate?: ArManualLinkCandidate) => {
    if (!isLowConfidenceGenericArBankCandidate(candidate)) return '';
    return 'Generic bank evidence only. Confirm this only if you know this transaction is the right bank proof for the receipt.';
};

const getArInvoiceCreditedAmount = (invoice?: ArCustomerDetailInvoice | ArInvoiceRecord | null) => (
    clampMoney(Number(invoice?.credited_amount || 0))
);

const describeArException = (
    exception: NonNullable<ArLedgerDetailResponse['linked_exceptions']>[number] | undefined,
    linkedInvoices: ArInvoiceRecord[] = [],
) => {
    const type = String(exception?.exception_type || '').trim();
    const invoiceId = String(exception?.invoice_id || '').trim();
    const linkedInvoice = linkedInvoices.find((invoice) => {
        const candidateId = String(invoice.invoice_id || invoice.source_id || '').trim();
        return candidateId && candidateId === invoiceId;
    });
    const invoiceLabel = linkedInvoice?.invoice_number || invoiceId || 'this invoice';
    const reason = String(exception?.reason || '').trim();

    if (type === 'pending_qbo_confirmation' || type === 'low_confidence_generic_counterparty') {
        return `Finz thinks invoice ${invoiceLabel} has an amount-only bank match. It is still low confidence because there is no strong counterparty or QBO payment link yet. Confirm it if the bank evidence looks right, or leave it for manual review.`;
    }
    if (type === 'manual_review_required') {
        return reason || 'Evidence exists, but the system does not have a safe enough match yet.';
    }
    if (type === 'stale_qbo_data') {
        return `Payment evidence points to invoice ${invoiceLabel}, but QBO still shows it open. Refresh QBO and review the payment trail.`;
    }
    if (type === 'partial_payment_unresolved') {
        return reason || `Payment evidence may only cover part of invoice ${invoiceLabel}. Review allocation before reconciling.`;
    }
    if (type === 'unmatched_receipt') {
        return 'This bank receipt is still unmatched. Add evidence or wait for a clearer QBO/payment trail.';
    }
    return reason || 'This row still has an exception that prevents clean reconciliation.';
};

const LedgerRecordDrawer: React.FC<LedgerRecordDrawerProps> = ({
    open,
    source,
    record,
    businessId,
    onClose,
    onReviewDocument,
    onOpenLinkedRecord,
    onOpenDocumentFile,
    onOpenArInvoice,
    onOpenArCustomer,
    onOpenApBill,
    onOpenQboTransaction,
    onOpenCashTransaction,
    onOpenArPayment,
}) => {
    const queryClient = useQueryClient();
    const [showArManualLinkPicker, setShowArManualLinkPicker] = useState(false);
    const [arManualSearch, setArManualSearch] = useState('');
    const [arManualNote, setArManualNote] = useState('');
    const [arManualMatchSection, setArManualMatchSection] = useState<'bank' | 'allocation'>('bank');
    const [selectedArManualBankId, setSelectedArManualBankId] = useState('');
    const [allocationDraft, setAllocationDraft] = useState<Record<string, string>>({});
    const [invoicePaymentDraft, setInvoicePaymentDraft] = useState<Record<string, string>>({});
    const [invoicePaymentScope, setInvoicePaymentScope] = useState<'customer' | 'all'>('customer');
    const [allocationInvoiceScope, setAllocationInvoiceScope] = useState<'customer' | 'all'>('customer');
    const [allocationInvoiceSearch, setAllocationInvoiceSearch] = useState('');
    const [expandedCustomerInvoiceIds, setExpandedCustomerInvoiceIds] = useState<string[]>([]);
    const [apExpectedPaymentDateOverride, setApExpectedPaymentDateOverride] = useState<{
        expected_payment_date?: string | null;
        user_expected_payment_date?: string | null;
        date_source?: string | null;
        status?: string;
        event_date?: string;
    } | null>(null);
    const [apRecurringForm, setApRecurringForm] = useState<ApRecurringFormState | null>(null);
    const [isRecurringAccountSelectorOpen, setIsRecurringAccountSelectorOpen] = useState(false);
    const [isVendorAccountSelectorOpen, setIsVendorAccountSelectorOpen] = useState(false);
    const [isRecurringAmountSuggestionsOpen, setIsRecurringAmountSuggestionsOpen] = useState(false);
    const [isRecurringDatePickerOpen, setIsRecurringDatePickerOpen] = useState(false);
    const recurringDatePickerRef = useRef<HTMLDivElement | null>(null);
    const isInventory = source === 'inventory';
    const isTransactionList = source === 'transaction_list';
    const isGeneralLedger = source === 'general_ledger';
    const isQboInvoice = source === 'invoices';
    const isQboBill = source === 'bills';
    const isArCustomer = source === 'ar_customers';
    const isApVendor = source === 'ap_vendors';
    const isApLedger = source === 'ap_ledger';
    const isArLedger = source === 'ar_ledger';
    const isUploadedInvoice = source === 'uploaded_invoices';
    const isUploadedExpense = source === 'uploaded_expenses';
    const isUploadedBill = source === 'uploaded_bills';
    const isUploaded = isUploadedInvoice || isUploadedExpense || isUploadedBill;
    const inventoryRecord = isInventory && record ? record as VendorLineItemRecord : null;
    const inventoryObservationRef = inventoryRecord
        ? Array.from(
            new Set(
                (inventoryRecord.item_records || [])
                    .map((item) => item._id)
                    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
            )
        )
        : [];
    const lineItems = (!isInventory && !isTransactionList && !isGeneralLedger)
        ? (((record as QboInvoiceRecord | QboBillRecord | UploadedLedgerRecord | null)?.data?.line_items) || [])
        : [];
    const uploadedRecord = isUploaded && record ? record as UploadedLedgerRecord : null;
    const uploadedStatus = uploadedRecord?.data.status;

    const { data: observationsData, isLoading: isObservationsLoading } = useQuery({
        queryKey: ['inventory-observations', inventoryObservationRef.join(','), inventoryRecord?._id, businessId],
        queryFn: () => ledgerViewsApi.getInventoryObservations(
            inventoryObservationRef.length > 0 ? inventoryObservationRef.join(',') : (inventoryRecord?._id || ''),
            businessId
        ),
        enabled: open && isInventory && !!inventoryRecord?._id,
    });
    const observations = observationsData?.items || [];
    const arCustomerRecord = isArCustomer && record ? record as ArCustomerRecord : null;
    const apVendorRecord = isApVendor && record ? record as ApVendorRecord : null;
    const apLedgerRecord = isApLedger && record ? record as ApLedgerRecord : null;
    const effectiveApLedgerRecord = apLedgerRecord ? {
        ...apLedgerRecord,
        ...(apExpectedPaymentDateOverride || {}),
    } : null;
    const arLedgerRecord = isArLedger && record ? record as ArLedgerRecord : null;
    const arRecordType = String(arLedgerRecord?.record_type || '').trim();
    const isArPaymentRecord = isArLedger && arRecordType === 'payment';
    const { data: arCustomerDetail } = useQuery({
        queryKey: ['ar-customer-detail', arCustomerRecord?.customer_id, businessId],
        queryFn: () => ledgerViewsApi.getArCustomerDetail(arCustomerRecord?.customer_id || '', businessId),
        enabled: open && isArCustomer && !!arCustomerRecord?.customer_id,
    });
    const apVendorLookupId = String(apVendorRecord?.vendor_id || apVendorRecord?.source_id || '').trim();
    const { data: apVendorDetail, isLoading: isApVendorDetailLoading } = useQuery({
        queryKey: ['ap-vendor-detail', apVendorLookupId, businessId],
        queryFn: () => ledgerViewsApi.getApVendorDetail(apVendorLookupId, businessId),
        enabled: open && isApVendor && !!apVendorLookupId,
    });
    const {
        data: arLedgerDetail,
        isLoading: isArLedgerDetailLoading,
        error: arLedgerDetailError,
    } = useQuery<ArLedgerDetailResponse>({
        queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId],
        queryFn: () => ledgerViewsApi.getArLedgerDetail(arLedgerRecord?.ledger_row_id || '', businessId),
        enabled: open && isArLedger && !!arLedgerRecord?.ledger_row_id,
        retry: (failureCount, error) => !isStaleArLedgerRowError(error) && failureCount < 3,
    });
    const { data: arOpenInvoicesPayload, isLoading: isArOpenInvoicesLoading } = useQuery({
        queryKey: ['ar-open-invoices', businessId],
        queryFn: () => ledgerViewsApi.getArOpenInvoices({ business_id: businessId, page: 1, page_size: 200 }),
        enabled: open && isArLedger && showArManualLinkPicker && isArPaymentRecord,
    });
    const {
        data: arManualLinkCandidatesPayload,
        isLoading: isArManualLinkCandidatesLoading,
        error: arManualLinkCandidatesError,
    } = useQuery({
        queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId, arManualSearch, invoicePaymentScope],
        queryFn: () => ledgerViewsApi.getArManualLinkCandidates(
            arLedgerRecord?.ledger_row_id || '',
            {
                business_id: businessId,
                search: arManualSearch || undefined,
                payment_scope: invoicePaymentScope,
                limit: 120,
            }
        ),
        enabled: open && isArLedger && !!arLedgerRecord?.ledger_row_id,
        retry: (failureCount, error) => !isStaleArLedgerRowError(error) && failureCount < 3,
    });
    const arLedgerDetailData = arLedgerDetail as ArLedgerDetailResponse | undefined;
    const arManualLinkCandidatesData = arManualLinkCandidatesPayload as {
        bank_candidates?: ArManualLinkCandidate[];
        payment_candidates?: ArManualLinkCandidate[];
        manual_binding?: ArManualCashBindingRecord | null;
        manual_allocations?: ArManualCashAllocationRecord[];
        manual_exclusions?: ArManualEvidenceExclusionRecord[];
        existing_override?: Record<string, any> | null;
    } | undefined;

    useEffect(() => {
        if (!open || !isArLedger) return;
        if (!isStaleArLedgerRowError(arLedgerDetailError)) return;
        setShowArManualLinkPicker(false);
        onClose();
    }, [open, isArLedger, arLedgerDetailError, onClose]);

    useEffect(() => {
        if (!showArManualLinkPicker) return;
        if (!isStaleArLedgerRowError(arManualLinkCandidatesError)) return;
        setShowArManualLinkPicker(false);
    }, [showArManualLinkPicker, arManualLinkCandidatesError]);
    const applyArManualLinkMutation = useMutation({
        mutationFn: () => ledgerViewsApi.applyArManualLink(
            arLedgerRecord?.ledger_row_id || '',
            {
                bank_transaction_id: selectedArManualBankId || undefined,
                note: arManualNote || undefined,
            },
            businessId
        ),
        onSuccess: async (response) => {
            mergeArLinkedEvidenceIntoCache();
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ar-open-invoices', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId] }),
            ]);
            setArManualNote('');
            if (response?.reconciliation_outcome && response.reconciliation_outcome.reconciled === false && isArPaymentRecord) {
                const reason = response.reconciliation_outcome.reason || 'no_invoice_link_resolved';
                alert(`Evidence match saved, but AR is still not reconciled (${reason}). The cash trail is linked, but invoice allocation still needs to be resolved.`);
                return;
            }
            setShowArManualLinkPicker(false);
        },
    });
    const updateApExpectedPaymentDateMutation = useMutation({
        mutationFn: (nextDate: Date | null) => ledgerViewsApi.updateApExpectedPaymentDate(
            apLedgerRecord?.entity_id || apLedgerRecord?.ledger_row_id || '',
            nextDate,
            businessId,
        ),
        onSuccess: async (response) => {
            const expectedPaymentDate = response.expected_payment_date ?? null;
            setApExpectedPaymentDateOverride({
                expected_payment_date: expectedPaymentDate,
                user_expected_payment_date: response.user_expected_payment_date ?? null,
                date_source: response.date_source ?? null,
                status: response.status || undefined,
                event_date: expectedPaymentDate || undefined,
            });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ap-vendor-detail'] }),
            ]);
        },
    });
    const updateApVendorAccountMutation = useMutation({
        mutationFn: (account: StandardAccount) => ledgerViewsApi.updateApVendorAccountingAccount(
            apVendorLookupId,
            account._id,
            businessId,
        ),
        onSuccess: async () => {
            setIsVendorAccountSelectorOpen(false);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ap-vendor-detail'] }),
            ]);
        },
        onError: (error) => {
            window.alert(getUserFriendlyErrorMessage(error, 'Unable to update vendor account.'));
        },
    });
    const invalidateApQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
            queryClient.invalidateQueries({ queryKey: ['ap-vendor-detail'] }),
        ]);
    };
    const upsertApRecurringOverrideMutation = useMutation({
        mutationFn: (payload: ApRecurringOverrideRequest) => ledgerViewsApi.upsertApRecurringOverride(payload, businessId),
        onSuccess: invalidateApQueries,
    });
    const upsertApRecurringEvidenceOverrideMutation = useMutation({
        mutationFn: (payload: ApRecurringEvidenceOverrideRequest) => ledgerViewsApi.upsertApRecurringEvidenceOverride(payload, businessId),
        onSuccess: invalidateApQueries,
    });

    useEffect(() => {
        setApExpectedPaymentDateOverride(null);
    }, [apLedgerRecord?.ledger_row_id, open]);
    useEffect(() => {
        if (!isRecurringDatePickerOpen) return undefined;
        const handlePointerDown = (event: MouseEvent) => {
            if (!recurringDatePickerRef.current) return;
            if (recurringDatePickerRef.current.contains(event.target as Node)) return;
            setIsRecurringDatePickerOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isRecurringDatePickerOpen]);
    const getRecurringMatchFields = (stream: ApRecurringStream) => ({
        match_vendor_family_key: String(stream.family_key || '').trim() || undefined,
        match_accounting_account_name: String(stream.accounting_account_name || '').trim() || undefined,
        match_accounting_category: String(stream.accounting_category || '').trim() || undefined,
        match_frequency: String(stream.frequency || '').trim() || undefined,
    });
    const handleEditApRecurring = (stream: ApRecurringStream) => {
        setApRecurringForm({
            mode: 'edit',
            targetStreamId: stream.ap_recurring_stream_id || undefined,
            matchVendorFamilyKey: String(stream.family_key || '').trim() || undefined,
            matchAccountingAccountName: String(stream.accounting_account_name || '').trim() || undefined,
            matchAccountingCategory: String(stream.accounting_category || '').trim() || undefined,
            matchFrequency: String(stream.frequency || '').trim() || undefined,
            vendorName: String(stream.vendor_name || '').trim(),
            accountingAccountName: String(stream.accounting_account_name || '').trim(),
            accountingCategory: String(stream.accounting_category || '').trim(),
            frequency: String(stream.frequency || stream.frequency_label || '').trim() || 'weekly',
            typicalAmount: typeof stream.typical_amount === 'number' ? String(stream.typical_amount) : '',
            nextExpectedPaymentDate: parseDatePreservingCalendar(stream.next_expected_payment_date || '') || null,
            suggestedAmounts: Array.from(
                new Set(
                    [
                        typeof stream.typical_amount === 'number' ? stream.typical_amount : null,
                        ...((stream.bank_evidence_rows || []).map((row) => row.amount ?? null)),
                        ...((stream.bill_evidence_rows || []).map((row) => row.amount ?? null)),
                    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0),
                ),
            ).sort((left, right) => right - left),
        });
        setIsRecurringAmountSuggestionsOpen(false);
        setIsRecurringDatePickerOpen(false);
    };
    const handleDisableApRecurring = (stream: ApRecurringStream) => {
        const label = [
            String(stream.vendor_name || '').trim() || 'Vendor',
            String(stream.accounting_account_name || '').trim() || 'Recurring stream',
        ].join(' - ');
        if (!window.confirm(`Disable this recurring stream?\n\n${label}`)) return;
        upsertApRecurringOverrideMutation.mutate({
            override_type: 'disable_stream',
            target_stream_id: stream.ap_recurring_stream_id || undefined,
            ...getRecurringMatchFields(stream),
        });
    };
    const handleAddApRecurring = () => {
        setApRecurringForm({
            mode: 'add',
            vendorName: String(apVendorRecord?.vendor_name || apVendorDetail?.vendor_name || '').trim(),
            accountingAccountName: String(apVendorDetail?.resolved_primary_accounting_account_name || apVendorRecord?.primary_accounting_account_name || '').trim(),
            accountingCategory: String(apVendorDetail?.resolved_primary_accounting_category || apVendorRecord?.primary_accounting_category || '').trim(),
            frequency: 'monthly',
            typicalAmount: '',
            nextExpectedPaymentDate: null,
            suggestedAmounts: Array.from(
                new Set(
                    [
                        ...((apVendorDetail?.recurring_streams || []).map((stream) => stream.typical_amount ?? null)),
                        ...((apVendorDetail?.bank_history || []).map((row) => row.amount ?? null)),
                    ]
                        .map((value) => (typeof value === 'number' ? Math.abs(value) : null))
                        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0),
                ),
            ).sort((left, right) => right - left).slice(0, 8),
        });
        setIsRecurringAmountSuggestionsOpen(false);
        setIsRecurringDatePickerOpen(false);
    };
    const handleExcludeApRecurringEvidence = (stream: ApRecurringStream, bankEvidence: ApRecurringBankEvidence) => {
        const transactionId = String(bankEvidence.transaction_id || '').trim();
        if (!transactionId) {
            window.alert('This bank evidence does not have a transaction id.');
            return;
        }
        if (!window.confirm('Exclude this bank evidence from recurring detection?')) return;
        upsertApRecurringEvidenceOverrideMutation.mutate({
            override_type: 'exclude_evidence',
            source_transaction_id: transactionId,
            target_stream_id: stream.ap_recurring_stream_id || undefined,
            target_stream_match_vendor_family_key: String(stream.family_key || '').trim() || undefined,
            target_stream_match_accounting_account_name: String(stream.accounting_account_name || '').trim() || undefined,
            target_stream_match_accounting_category: String(stream.accounting_category || '').trim() || undefined,
            target_vendor_name: String(stream.vendor_name || bankEvidence.vendor_name || '').trim() || undefined,
        });
    };
    const submitApRecurringForm = () => {
        if (!apRecurringForm) return;
        const parsedAmount = Number(apRecurringForm.typicalAmount);
        if (!apRecurringForm.vendorName.trim()) {
            window.alert('Vendor name is required.');
            return;
        }
        if (!apRecurringForm.accountingAccountName.trim()) {
            window.alert('Account name is required.');
            return;
        }
        if (!apRecurringForm.frequency.trim()) {
            window.alert('Frequency is required.');
            return;
        }
        if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            window.alert('Typical amount must be a positive number.');
            return;
        }
        if (!apRecurringForm.nextExpectedPaymentDate) {
            window.alert('Next expected payment date is required.');
            return;
        }
        const payload: ApRecurringOverrideRequest = apRecurringForm.mode === 'edit'
            ? {
                override_type: 'update_stream',
                target_stream_id: apRecurringForm.targetStreamId,
                match_vendor_family_key: apRecurringForm.matchVendorFamilyKey,
                match_accounting_account_name: apRecurringForm.matchAccountingAccountName,
                match_accounting_category: apRecurringForm.matchAccountingCategory,
                match_frequency: apRecurringForm.matchFrequency,
                vendor_name: apRecurringForm.vendorName.trim(),
                accounting_account_name: apRecurringForm.accountingAccountName.trim(),
                accounting_category: apRecurringForm.accountingCategory.trim() || undefined,
                frequency: apRecurringForm.frequency.trim(),
                typical_amount: parsedAmount,
                next_expected_payment_date: apRecurringForm.nextExpectedPaymentDate.toISOString().slice(0, 10),
            }
            : {
                override_type: 'manual_stream',
                vendor_name: apRecurringForm.vendorName.trim(),
                accounting_account_name: apRecurringForm.accountingAccountName.trim(),
                accounting_category: apRecurringForm.accountingCategory.trim() || undefined,
                frequency: apRecurringForm.frequency.trim(),
                typical_amount: parsedAmount,
                next_expected_payment_date: apRecurringForm.nextExpectedPaymentDate.toISOString().slice(0, 10),
            };
        upsertApRecurringOverrideMutation.mutate(payload, {
            onSuccess: async () => {
                setApRecurringForm(null);
                setIsRecurringAmountSuggestionsOpen(false);
                setIsRecurringDatePickerOpen(false);
                await invalidateApQueries();
            },
        });
    };
    const handleSelectRecurringAccount = (account: StandardAccount) => {
        const displayName = String(account.display_name || account.account_name || '').trim();
        const bucket = String(account.fiscal_bucket || '').trim();
        setApRecurringForm((current) => current ? {
            ...current,
            accountingAccountName: displayName,
            accountingCategory: bucket || current.accountingCategory,
        } : current);
        setIsRecurringAccountSelectorOpen(false);
    };
    const removeArManualLinkMutation = useMutation({
        mutationFn: () => ledgerViewsApi.removeArManualLink(arLedgerRecord?.ledger_row_id || '', businessId),
        onSuccess: async () => {
            queryClient.setQueryData(
                ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId, arManualSearch],
                (existing: any) => existing ? {
                    ...existing,
                    existing_override: null,
                    manual_binding: null,
                    manual_allocations: [],
                    manual_exclusions: [],
                } : existing
            );
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId] }),
            ]);
            setSelectedArManualBankId('');
            setArManualNote('');
            setShowArManualLinkPicker(false);
        },
    });
    const removeArLinkedEvidenceMutation = useMutation({
        mutationFn: (payload: { linked_source_kind: 'bank' | 'bank_receipt' | 'payment' | 'qbo_payment' | 'invoice' | 'ar_invoice'; linked_source_id: string }) =>
            ledgerViewsApi.removeArLinkedEvidence(arLedgerRecord?.ledger_row_id || '', payload, businessId),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ar-open-invoices', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId] }),
            ]);
        },
    });
    const applyArManualAllocationMutation = useMutation({
        mutationFn: () => ledgerViewsApi.applyArManualAllocation(
            arLedgerRecord?.ledger_row_id || '',
            {
                allocations: allocationInvoices
                    .map((invoice) => {
                        const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                        const amount = clampMoney(Number(allocationDraft[invoiceId] || 0));
                        return invoiceId && amount > 0 ? { invoice_id: invoiceId, amount } : null;
                    })
                    .filter((item): item is { invoice_id: string; amount: number } => !!item),
            },
            businessId
        ),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ar-open-invoices', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId] }),
            ]);
            setShowArManualLinkPicker(false);
        },
    });
    const applyArManualInvoiceAllocationMutation = useMutation({
        mutationFn: () => ledgerViewsApi.applyArManualInvoiceAllocation(
            arLedgerRecord?.ledger_row_id || '',
            {
                payments: arInvoicePaymentCandidates
                    .map((candidate) => {
                        const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
                        const amount = clampMoney(Number(invoicePaymentDraft[paymentSourceId] || 0));
                        return paymentSourceId && amount > 0 ? { payment_source_id: paymentSourceId, amount } : null;
                    })
                    .filter((item): item is { payment_source_id: string; amount: number } => !!item),
            },
            businessId
        ),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ar-open-invoices', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId] }),
                queryClient.refetchQueries({ queryKey: ['ar-manual-link-candidates', arLedgerRecord?.ledger_row_id, businessId] }),
            ]);
            setShowArManualLinkPicker(false);
        },
    });
    const allocationLinkedPayments = arLedgerDetailData?.linked_payments || [];
    const allocationLinkedBankReceipts = arLedgerDetailData?.linked_bank_receipts || [];
    const allocationInvoicePool = useMemo(() => (
        (arOpenInvoicesPayload?.items || []).filter((invoice) => {
            const invoiceCustomerId = String(invoice.customer_id || '').trim();
            const invoiceCustomerName = String(invoice.customer_name || '').trim().toLowerCase();
            const rowCustomerId = String(arLedgerRecord?.customer_id || '').trim();
            const rowCustomerName = String(arLedgerRecord?.customer_name || '').trim().toLowerCase();
            if (rowCustomerId && invoiceCustomerId) {
                return invoiceCustomerId === rowCustomerId;
            }
            if (rowCustomerName && invoiceCustomerName) {
                return invoiceCustomerName === rowCustomerName;
            }
            return false;
        })
    ), [arOpenInvoicesPayload?.items, arLedgerRecord?.customer_id, arLedgerRecord?.customer_name]);
    const allocationHistoricalInvoices = useMemo(() => (
        Array.from(
            new Map(
                [
                    ...((arLedgerDetailData?.linked_cash_applications || []).map((application) => ({
                        invoice_id: application.invoice_id,
                        source_id: application.invoice_id,
                        invoice_number: application.invoice_id,
                        customer_id: arLedgerRecord?.customer_id,
                        customer_name: arLedgerRecord?.customer_name,
                        open_amount: 0,
                        open_amount_finz: 0,
                    })) as ArInvoiceRecord[]),
                    ...(((arLedgerDetailData?.linked_invoices || []) as ArInvoiceRecord[])),
                ].map((invoice) => {
                    const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                    return [invoiceId, invoice] as const;
                }).filter((entry) => entry[0])
            ).values()
        )
    ), [arLedgerDetailData?.linked_invoices, arLedgerDetailData?.linked_cash_applications, arLedgerRecord?.customer_id, arLedgerRecord?.customer_name]);
    const allocationCustomerInvoices = useMemo(() => (
        Array.from(
            new Map(
                [
                    ...allocationInvoicePool,
                    ...allocationHistoricalInvoices,
                ].map((invoice) => {
                    const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                    return [invoiceId, invoice] as const;
                }).filter((entry) => entry[0])
            ).values()
        )
    ), [allocationInvoicePool, allocationHistoricalInvoices]);
    const allocationAllInvoices = useMemo(() => (
        Array.from(
            new Map(
                [
                    ...((arOpenInvoicesPayload?.items || []) as ArInvoiceRecord[]),
                    ...allocationHistoricalInvoices,
                ].map((invoice) => {
                    const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                    return [invoiceId, invoice] as const;
                }).filter((entry) => entry[0])
            ).values()
        )
    ), [arOpenInvoicesPayload?.items, allocationHistoricalInvoices]);
    const normalizedAllocationInvoiceSearch = allocationInvoiceSearch.trim().toLowerCase();
    const allocationInvoices = useMemo(() => (
        (allocationInvoiceScope === 'all' ? allocationAllInvoices : allocationCustomerInvoices).filter((invoice) => {
            if (!normalizedAllocationInvoiceSearch) return true;
            const haystack = [
                invoice.invoice_number,
                invoice.invoice_id,
                invoice.source_id,
                invoice.customer_name,
                invoice.customer_id,
                invoice.due_date,
            ]
                .map((value) => String(value || '').toLowerCase())
                .join(' ');
            return haystack.includes(normalizedAllocationInvoiceSearch);
        })
    ), [allocationInvoiceScope, allocationAllInvoices, allocationCustomerInvoices, normalizedAllocationInvoiceSearch]);
    const allocationHasEvidence = allocationLinkedPayments.length > 0 || allocationLinkedBankReceipts.length > 0;
    const allocationPaymentTotal = clampMoney(Number(arLedgerRecord?.amount || 0));
    const allocationDraftTotal = clampMoney(
        Object.values(allocationDraft).reduce((sum, rawValue) => sum + clampMoney(Number(rawValue || 0)), 0)
    );
    const allocationRemaining = clampMoney(allocationPaymentTotal - allocationDraftTotal);

    const mergeArLinkedEvidenceIntoCache = () => {
        const detailKey = ['ar-ledger-detail', arLedgerRecord?.ledger_row_id, businessId];
        queryClient.setQueryData(detailKey, (current: ArLedgerDetailResponse | undefined) => {
            if (!current) return current;
            const next: ArLedgerDetailResponse = {
                ...current,
                linked_bank_receipts: [...(current.linked_bank_receipts || [])],
            };

            if (selectedArManualBankCandidate) {
                const bankSourceId = String(selectedArManualBankCandidate.id || '').trim();
                if (
                    bankSourceId
                    && !(next.linked_bank_receipts || []).some((receipt) => String(receipt.source_id || '').trim() === bankSourceId)
                ) {
                    next.linked_bank_receipts = [
                        ...(next.linked_bank_receipts || []),
                        {
                            source_id: bankSourceId,
                            receipt_date: selectedArManualBankCandidate.transaction_date || undefined,
                            receipt_amount: Number(selectedArManualBankCandidate.amount || 0),
                            counterparty_name: selectedArManualBankCandidate.counterparty_name || undefined,
                            status: 'matched',
                            customer_id: arLedgerRecord?.customer_id || null,
                            linked_payment_id: null,
                        },
                    ];
                }
            }

            return next;
        });
    };

    useEffect(() => {
        if (!showArManualLinkPicker || !isArPaymentRecord) return;
        const nextDraft: Record<string, string> = {};
        const existingApplications = (arLedgerDetailData?.linked_cash_applications || []).map((application) => ({
            invoiceId: String(application.invoice_id || '').trim(),
            allocatedAmount: clampMoney(Number(application.allocated_amount || 0)),
        }));
        allocationInvoices.forEach((invoice) => {
            const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
            if (!invoiceId) return;
            const invoiceSourceId = String(invoice.source_id || '').trim();
            const existingAmount = existingApplications.find((application) => (
                application.allocatedAmount > 0
                && (
                    application.invoiceId === invoiceId
                    || (invoiceSourceId && application.invoiceId === invoiceSourceId)
                )
            ))?.allocatedAmount;
            if (existingAmount !== undefined) {
                nextDraft[invoiceId] = existingAmount.toFixed(2);
            } else {
                nextDraft[invoiceId] = '';
            }
        });
        setAllocationDraft((current) => {
            const currentKeys = Object.keys(current);
            const nextKeys = Object.keys(nextDraft);
            if (
                currentKeys.length === nextKeys.length
                && nextKeys.every((key) => current[key] === nextDraft[key])
            ) {
                return current;
            }
            return nextDraft;
        });
    }, [showArManualLinkPicker, isArPaymentRecord, arLedgerDetailData?.linked_cash_applications, allocationInvoices]);

    useEffect(() => {
        if (!open || !isArCustomer) {
            setExpandedCustomerInvoiceIds([]);
        }
    }, [open, isArCustomer, record]);
    const inventoryUnitSeries = isInventory
        ? (
            (inventoryRecord?.unit_series && inventoryRecord.unit_series.length > 0
                ? inventoryRecord.unit_series
                : [{
                    series_id: `${inventoryRecord?.comparable_unit_family || 'unknown'}:${inventoryRecord?.comparable_unit || 'unknown'}`,
                    comparable_unit: inventoryRecord?.comparable_unit,
                    comparable_unit_family: inventoryRecord?.comparable_unit_family,
                    most_recent_unit_price: inventoryRecord?.most_recent_unit_price,
                    lowest_unit_price: inventoryRecord?.lowest_unit_price,
                    observation_count: inventoryRecord?.observation_count,
                    item_records: inventoryRecord?.item_records || [],
                }])
        ).map((series) => {
            const recordIds = new Set((series.item_records || []).map((item) => item._id).filter(Boolean));
            const vendorRows = (series.item_records || [])
                .filter((item) => !!item.vendor_name)
                .sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''));
            const orderedSeriesObservations = [...observations]
                .filter((observation) => {
                    if (recordIds.size > 0 && observation.catalog_id) {
                        return recordIds.has(observation.catalog_id);
                    }
                    const observationUnit = observation.comparable_unit || observation.description;
                    return observationUnit === series.comparable_unit;
                })
                .sort((left, right) => {
                    const effectiveDiff = getObservationSortTime(left.effective_at || left.invoice_date) - getObservationSortTime(right.effective_at || right.invoice_date);
                    if (effectiveDiff !== 0) return effectiveDiff;
                    const seenDiff = getObservationSortTime(left.seen_at) - getObservationSortTime(right.seen_at);
                    if (seenDiff !== 0) return seenDiff;
                    return (left._id || '').localeCompare(right._id || '');
                });
            const chartData = orderedSeriesObservations
                .filter((observation) => typeof observation.unit_price === 'number')
                .map((observation, index) => ({
                    id: observation._id || `observation-${index}`,
                    label: formatDate(getObservationDisplayDate(observation)),
                    dateLabel: formatDate(getObservationDisplayDate(observation)),
                    price: observation.unit_price || 0,
                    invoiceLabel: observation.document_number || observation.invoice_id || '—',
                    vendorName: observation.vendor_name || inventoryRecord?.vendor_name || '—',
                }));
            return {
                ...series,
                vendorRows,
                observations: orderedSeriesObservations,
                chartData,
            };
        })
        : [];
    const inventoryChartCurrency = inventoryRecord?.currency || observations[0]?.currency || 'USD';
    const qboBillRecord = isQboBill && record ? record as QboBillRecord : null;
    const getArInvoiceOpenAmount = (invoice?: ArCustomerDetailInvoice | null) => invoice?.open_amount_finz ?? invoice?.open_amount ?? 0;
    const getArInvoicePaidAmount = (invoice?: ArCustomerDetailInvoice | null) => invoice?.paid_amount ?? Math.max((invoice?.original_amount || 0) - getArInvoiceOpenAmount(invoice), 0);
    const getArInvoiceStatus = (invoice?: ArCustomerDetailInvoice | null) => invoice?.invoice_status_finz || invoice?.status || 'open';
    const renderArInvoiceLink = (invoiceId?: string | null, invoiceNumber?: string | null, fallbackLabel?: string | null) => {
        const resolvedInvoiceId = String(invoiceId || '').trim();
        const label = invoiceNumber || fallbackLabel || 'Invoice';
        if (resolvedInvoiceId && onOpenArInvoice) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenArInvoice(resolvedInvoiceId)}
                >
                    {label}
                </button>
            );
        }
        return label || '—';
    };
    const renderArCustomerLink = (customerId?: string | null, customerName?: string | null) => {
        const resolvedCustomerId = String(customerId || '').trim();
        const label = customerName || 'Customer';
        if (resolvedCustomerId && onOpenArCustomer) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenArCustomer(resolvedCustomerId, customerName || undefined)}
                >
                    {label}
                </button>
            );
        }
        return label || '—';
    };
    const renderPaymentLink = (transactionId?: string | null, label?: string | null) => {
        const resolvedTransactionId = String(transactionId || '').trim();
        const resolvedLabel = String(label || '').trim() || 'Customer payment';
        if (resolvedTransactionId && onOpenArPayment) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenArPayment(resolvedTransactionId)}
                >
                    {resolvedLabel}
                </button>
            );
        }
        return resolvedLabel;
    };
    const renderBankReceiptLink = (transactionId?: string | null, label?: string | null) => {
        const resolvedTransactionId = String(transactionId || '').trim();
        const resolvedLabel = String(label || '').trim() || 'Bank receipt';
        if (resolvedTransactionId && onOpenCashTransaction) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenCashTransaction(resolvedTransactionId)}
                >
                    {resolvedLabel}
                </button>
            );
        }
        return resolvedLabel;
    };
    const renderQboTransactionLink = (transactionId?: string | null, label?: string | null) => {
        const resolvedTransactionId = String(transactionId || '').trim();
        const resolvedLabel = String(label || '').trim() || 'QBO transaction';
        if (resolvedTransactionId && onOpenQboTransaction) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenQboTransaction(resolvedTransactionId)}
                >
                    {resolvedLabel}
                </button>
            );
        }
        return resolvedLabel;
    };
    const renderApBillLink = (billId?: string | null, label?: string | null) => {
        const resolvedBillId = String(billId || '').trim();
        const resolvedLabel = String(label || '').trim() || 'Bill';
        if (resolvedBillId && onOpenApBill) {
            return (
                <button
                    type="button"
                    className="text-left text-indigo-600 hover:underline"
                    onClick={() => onOpenApBill(resolvedBillId)}
                >
                    {resolvedLabel}
                </button>
            );
        }
        return resolvedLabel;
    };
    const arManualBankCandidates = (arManualLinkCandidatesData?.bank_candidates || []) as ArManualLinkCandidate[];
    const arManualPaymentCandidates = (arManualLinkCandidatesData?.payment_candidates || []) as ArManualLinkCandidate[];
    const arManualExistingOverride = buildLegacyOverrideShape(
        arManualLinkCandidatesData?.manual_binding,
        arManualLinkCandidatesData?.manual_allocations,
        arManualLinkCandidatesData?.manual_exclusions,
        arManualLinkCandidatesData?.existing_override as Record<string, any> | null | undefined,
    );
    const hasArManualOverride = Boolean(
        String(arManualExistingOverride?.qbo_transaction_list_id || '').trim()
        || String(arManualExistingOverride?.qbo_transaction_type_id || '').trim()
        || String(arManualExistingOverride?.bank_transaction_id || '').trim()
        || String(arManualExistingOverride?.linked_invoice_id || '').trim()
        || ((arManualExistingOverride?.manual_allocations as unknown[]) || []).length > 0
    );
    const arManualOverrideBankSummary = arManualExistingOverride?.bank_summary as Record<string, any> | undefined;
    const selectedArManualBankCandidate = arManualBankCandidates.find((candidate) => candidate.id === selectedArManualBankId);
    const selectedArManualBankIsLowConfidenceGeneric = isLowConfidenceGenericArBankCandidate(selectedArManualBankCandidate);
    const arInvoicePaymentCandidates = useMemo(() => {
        const searchValue = arManualSearch.trim().toLowerCase();
        return arManualPaymentCandidates.filter((candidate) => {
            const sourceId = String(candidate.source_id || candidate.id || '').trim();
            if (!sourceId) return false;
            if (!searchValue) return true;
            return [
                sourceId.toLowerCase(),
                String(candidate.customer_name || candidate.counterparty_name || '').trim().toLowerCase(),
                String(candidate.document_number || '').trim().toLowerCase(),
                String(candidate.amount || '').trim(),
                String(candidate.open_amount || '').trim(),
            ].some((value) => value.includes(searchValue));
        });
    }, [arManualPaymentCandidates, arManualSearch]);
    const invoiceCurrentAppliedTotal = isArPaymentRecord
        ? 0
        : clampMoney(
            (arLedgerDetailData?.linked_cash_applications || []).reduce(
                (sum, application) => sum + clampMoney(Number(application.allocated_amount || 0)),
                0
            )
        );
    const invoiceDraftAppliedTotal = isArPaymentRecord
        ? 0
        : clampMoney(
            arInvoicePaymentCandidates.reduce((sum, candidate) => {
                const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
                return sum + clampMoney(Number(invoicePaymentDraft[paymentSourceId] || 0));
            }, 0)
        );
    const invoiceCurrentOpenAmount = isArPaymentRecord ? 0 : clampMoney(Number(arLedgerRecord?.open_amount || 0));
    const invoiceProjectedOpenAmount = isArPaymentRecord
        ? 0
        : clampMoney(Math.max(invoiceCurrentOpenAmount + invoiceCurrentAppliedTotal - invoiceDraftAppliedTotal, 0));
    const invoiceExistingAllocationByPaymentSourceId = useMemo(() => {
        if (isArPaymentRecord) return new Map<string, number>();
        const existingApplications = (arLedgerDetailData?.linked_cash_applications || []).map((application) => ({
            paymentSourceId: String(application.payment_id || application.bank_receipt_id || '').trim(),
            allocatedAmount: clampMoney(Number(application.allocated_amount || 0)),
        }));
        const linkedPaymentAliasBySourceId = new Map<string, Set<string>>();
        (arLedgerDetailData?.linked_payments || []).forEach((payment) => {
            const paymentSourceId = String(payment.source_id || '').trim();
            if (!paymentSourceId) return;
            const aliases = new Set<string>([paymentSourceId]);
            const linkedBankReceiptIds = (((payment as Record<string, unknown>).linked_bank_receipt_ids as unknown[]) || []);
            linkedBankReceiptIds.forEach((receiptId: unknown) => {
                const normalizedReceiptId = String(receiptId || '').trim();
                if (normalizedReceiptId) aliases.add(normalizedReceiptId);
            });
            linkedPaymentAliasBySourceId.set(paymentSourceId, aliases);
        });
        const allocationMap = new Map<string, number>();
        arInvoicePaymentCandidates.forEach((candidate) => {
            const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
            if (!paymentSourceId) return;
            const candidateAliases = linkedPaymentAliasBySourceId.get(paymentSourceId) || new Set<string>([paymentSourceId]);
            const existingAmount = existingApplications.find((application) => (
                candidateAliases.has(application.paymentSourceId)
                && application.allocatedAmount > 0
            ))?.allocatedAmount;
            allocationMap.set(paymentSourceId, existingAmount !== undefined ? existingAmount : 0);
        });
        return allocationMap;
    }, [isArPaymentRecord, arLedgerDetailData?.linked_cash_applications, arLedgerDetailData?.linked_payments, arInvoicePaymentCandidates]);
    useEffect(() => {
        if (!showArManualLinkPicker || isArPaymentRecord) return;
        const nextDraft: Record<string, string> = {};
        arInvoicePaymentCandidates.forEach((candidate) => {
            const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
            if (!paymentSourceId) return;
            const existingAmount = invoiceExistingAllocationByPaymentSourceId.get(paymentSourceId);
            nextDraft[paymentSourceId] = existingAmount !== undefined ? existingAmount.toFixed(2) : '';
        });
        setInvoicePaymentDraft((current) => {
            const currentKeys = Object.keys(current);
            const nextKeys = Object.keys(nextDraft);
            if (
                currentKeys.length === nextKeys.length
                && nextKeys.every((key) => current[key] === nextDraft[key])
            ) {
                return current;
            }
            return nextDraft;
        });
    }, [showArManualLinkPicker, isArPaymentRecord, arInvoicePaymentCandidates, invoiceExistingAllocationByPaymentSourceId]);
    const allocationPreviewItems = allocationInvoices
        .map((invoice) => {
            const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
            const amount = clampMoney(Number(allocationDraft[invoiceId] || 0));
            if (!invoiceId || amount <= 0) return null;
            return {
                invoiceId,
                invoiceNumber: String(invoice.invoice_number || invoiceId).trim() || invoiceId,
                amount,
                creditedAmount: getArInvoiceCreditedAmount(invoice),
            };
        })
        .filter((item): item is { invoiceId: string; invoiceNumber: string; amount: number; creditedAmount: number } => !!item);

    useEffect(() => {
        if (!showArManualLinkPicker) return;
        if (!selectedArManualBankId) {
            const overrideBankId = String(arManualExistingOverride?.bank_transaction_id || '').trim();
            const linkedBankIds = new Set(
                (allocationLinkedBankReceipts || [])
                    .map((receipt) => String(receipt.source_id || '').trim())
                    .filter(Boolean)
            );
            const nextBankCandidate = arManualBankCandidates.find((candidate) => {
                const candidateId = String(candidate.id || '').trim();
                if (overrideBankId && candidateId === overrideBankId) return true;
                if (linkedBankIds.has(candidateId)) return true;
                return false;
            }) || arManualBankCandidates.find((candidate) => {
                const delta = Number(candidate.amount_delta);
                return isLowConfidenceGenericArBankCandidate(candidate) && Number.isFinite(delta) && Math.abs(delta) < 0.005;
            }) || arManualBankCandidates.find((candidate) => isLowConfidenceGenericArBankCandidate(candidate));
            if (nextBankCandidate?.id) {
                setSelectedArManualBankId(String(nextBankCandidate.id));
            }
        }
    }, [
        showArManualLinkPicker,
        arManualExistingOverride,
        arManualBankCandidates,
        allocationLinkedBankReceipts,
        selectedArManualBankId,
    ]);

    useEffect(() => {
        if (!showArManualLinkPicker) return;
        setArManualMatchSection('bank');
    }, [showArManualLinkPicker, arLedgerRecord?.ledger_row_id]);

    useEffect(() => {
        if (!showArManualLinkPicker) return;
        setAllocationInvoiceScope('customer');
        setAllocationInvoiceSearch('');
    }, [showArManualLinkPicker, arLedgerRecord?.ledger_row_id]);

    if (!open || !record) return null;

    let title = 'Ledger Detail';
    let subtitle: React.ReactNode = null;
    let amountDisplay: React.ReactNode = null;
    let metadataContent: React.ReactNode = null;

    if (isInventory && inventoryRecord) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildInventoryDrawerPresentation({
            inventoryRecord,
            inventoryUnitSeries,
            inventoryChartCurrency,
            isObservationsLoading,
            formatDate,
            onOpenLinkedRecord,
        }));
    } else if (isTransactionList) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildTransactionListDrawerPresentation({
            txn: record as QboTransactionListItem,
            formatDate,
            formatTitleCase,
            formatTransactionAmount,
            onOpenQboTransaction,
        }));
    } else if (isGeneralLedger) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildGeneralLedgerDrawerPresentation({
            gl: record as QboGeneralLedgerRecord,
            formatDate,
        }));
    } else if (isArCustomer) {
        const customer = arCustomerRecord;
        const detail = arCustomerDetail as ArCustomerDetailResponse | undefined;
        const customerInvoices = [...(detail?.invoices || [])].sort(
            (left, right) => getArInvoiceSortTime(right) - getArInvoiceSortTime(left)
        );
        const qboPayments = detail?.qbo_payments || [];
        const linkedBankReceipts = detail?.bank_receipt_coverage?.linked_bank_receipts || [];
        const cashApplications = detail?.cash_applications || [];
        const bankReceiptsById = new Map(
            linkedBankReceipts
                .map((receipt) => {
                    const receiptId = String(receipt.source_id || '').trim();
                    return receiptId ? [receiptId, receipt] as const : null;
                })
                .filter((entry): entry is readonly [string, typeof linkedBankReceipts[number]] => !!entry)
        );
        const invoiceLinkRows: ArCustomerInvoiceLinkRow[] = customerInvoices.map((invoice) => {
            const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
            const invoiceNumber = invoice.invoice_number || invoiceId || '—';
            const matchedPayments = qboPayments.filter((payment: ArCustomerDetailPayment) => (payment.linked_invoice_ids || []).includes(invoiceId));
            const matchedPaymentIds = new Set(
                matchedPayments
                    .map((payment: ArCustomerDetailPayment) => String(payment.source_id || '').trim())
                    .filter((value) => value.length > 0)
            );
            const directBankReceipts = linkedBankReceipts.filter((receipt: ArCustomerDetailBankReceipt) => String(receipt.linked_invoice_id || '').trim() === invoiceId);
            const bridgedBankReceipts = matchedPayments.flatMap((payment: ArCustomerDetailPayment) =>
                (payment.linked_bank_receipt_ids || [])
                    .map((receiptId: string) => bankReceiptsById.get(String(receiptId || '').trim()))
                    .filter((receipt): receipt is ArCustomerDetailBankReceipt => !!receipt)
            );
            const allBankReceipts = Array.from(
                new Map(
                    [...directBankReceipts, ...bridgedBankReceipts]
                        .map((receipt: ArCustomerDetailBankReceipt) => {
                            const receiptId = String(receipt.source_id || '').trim();
                            return receiptId ? [receiptId, receipt] as const : null;
                        })
                        .filter((entry): entry is readonly [string, ArCustomerDetailBankReceipt] => !!entry)
                ).values()
            );
            const applications = cashApplications.filter((application: ArCustomerDetailCashApplication) => String(application.invoice_id || '').trim() === invoiceId);
            const allocatedTotal = clampMoney(applications.reduce((sum, application: ArCustomerDetailCashApplication) => sum + Number(application.allocated_amount || 0), 0));
            const evidenceTotal = clampMoney(
                matchedPayments.reduce((sum, payment: ArCustomerDetailPayment) => sum + Number(payment.payment_amount || 0), 0)
                + directBankReceipts
                    .filter((receipt: ArCustomerDetailBankReceipt) => !String(receipt.linked_payment_id || '').trim() || !matchedPaymentIds.has(String(receipt.linked_payment_id || '').trim()))
                    .reduce((sum, receipt: ArCustomerDetailBankReceipt) => sum + Number(receipt.receipt_amount || 0), 0)
            );
            return {
                invoice,
                invoiceId,
                invoiceNumber,
                matchedPayments,
                bankReceipts: allBankReceipts,
                applications,
                allocatedTotal,
                evidenceTotal,
            };
        });
        ({ title, subtitle, amountDisplay, metadataContent } = buildArCustomerDrawerPresentation({
            customer: customer!,
            detail,
            invoiceLinkRows,
            expandedCustomerInvoiceIds,
            setExpandedCustomerInvoiceIds,
            formatDate,
            formatPercent,
            getArInvoiceStatus,
            getArInvoiceOpenAmount,
            getArInvoicePaidAmount,
            getArInvoiceCreditedAmount,
            compactStatusTone,
            renderArInvoiceLink,
            renderPaymentLink,
            renderBankReceiptLink,
            getFriendlyPaymentLabel,
            getFriendlyBankReceiptLabel,
            getCreditMemoSummary,
        }));
    } else if (isArLedger) {
        const ar = arLedgerRecord;
        const detail = arLedgerDetail as ArLedgerDetailResponse | undefined;
        const recordType = String(ar?.record_type || '').trim();
        const reconciliationStatus = String(ar?.reconciliation_status || '').trim();
        const isPayment = recordType === 'payment';
        const isInvoice = recordType === 'invoice';
        const canManualLink = ['unreconciled', 'manual_review_required', 'detected'].includes(reconciliationStatus) || isPayment || isInvoice;
        const confidenceScore = ar?.confidence_score ?? 0;
        const paymentEvidenceSources = (ar?.payment_evidence_sources || []).filter(Boolean);
        const linkedInvoices = [...(detail?.linked_invoices || [])].sort(
            (left, right) => getArInvoiceSortTime(right) - getArInvoiceSortTime(left)
        );
        const linkedPayments = detail?.linked_payments || [];
        const linkedBankReceipts = detail?.linked_bank_receipts || [];
        const linkedCashApplications = detail?.linked_cash_applications || [];
        const linkedCreditApplications = detail?.linked_credit_applications || [];
        const linkedExceptions = detail?.linked_exceptions || [];
        const genericLowConfidenceExceptions = linkedExceptions.filter((exception) => String(exception.exception_type || '').trim() === 'low_confidence_generic_counterparty');
        const hasEvidence = linkedPayments.length > 0 || linkedBankReceipts.length > 0;
        const hasLowConfidenceSuggestedEvidence = genericLowConfidenceExceptions.length > 0 && !hasEvidence;
        const hasApplications = linkedCashApplications.length > 0;
        const isFullyReconciled = reconciliationStatus === 'reconciled';
        const paymentTotal = allocationPaymentTotal;
        const canOpenAllocation = isPayment && hasEvidence;
        const arFriendlyReference = isPayment
            ? (ar?.reference_no || getFriendlyPaymentLabel(linkedPayments[0], ar?.customer_name))
            : (ar?.reference_no || 'Invoice');
        const evidenceButtonLabel = hasLowConfidenceSuggestedEvidence
            ? 'Review Suggested Evidence'
            : (hasEvidence ? 'Change Evidence' : 'Add Evidence');
        const allocationButtonLabel = hasApplications ? 'Edit Allocation' : 'Apply To Invoice';
        const showAllocationAction = isPayment && canOpenAllocation;
        ({ title, subtitle, amountDisplay, metadataContent } = buildArLedgerDrawerPresentation({
            ar: ar!,
            detail,
            isArLedgerDetailLoading,
            hasArManualOverride,
            canManualLink,
            showAllocationAction,
            evidenceButtonLabel,
            allocationButtonLabel,
            onOpenManualLink: () => setShowArManualLinkPicker(true),
            onRemoveManualOverride: () => removeArManualLinkMutation.mutate(),
            removeManualLinkPending: applyArManualLinkMutation.isPending || removeArManualLinkMutation.isPending,
            formatDate,
            formatPercent,
            formatTitleCase,
            compactStatusTone,
            formatConfidencePercent,
            renderArCustomerLink,
            renderArInvoiceLink,
            renderPaymentLink,
            renderBankReceiptLink,
            renderQboTransactionLink,
            getFriendlyPaymentLabel,
            getFriendlyBankReceiptLabel,
            getFriendlyBankReceiptStatus,
            getCreditMemoSummary,
            getPaymentSourceSummary,
            isNonBankLinkedPayment,
            describeArException,
        }));
    } else if (isApVendor) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildApVendorDrawerPresentation({
            vendor: apVendorRecord!,
            vendorDetail: apVendorDetail,
            isLoading: isApVendorDetailLoading,
            isUpdatingRecurringOverride: upsertApRecurringOverrideMutation.isPending || upsertApRecurringEvidenceOverrideMutation.isPending,
            formatDate,
            formatPercent,
            formatTitleCase,
            renderApBillLink,
            renderQboTransactionLink,
            renderBankReceiptLink,
            onEditRecurring: handleEditApRecurring,
            onDisableRecurring: handleDisableApRecurring,
            onAddRecurring: handleAddApRecurring,
            onOpenAccountSelector: () => setIsVendorAccountSelectorOpen(true),
            isUpdatingAccount: updateApVendorAccountMutation.isPending,
            onExcludeRecurringEvidence: handleExcludeApRecurringEvidence,
        }));
    } else if (isQboInvoice) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildQboInvoiceDrawerPresentation({
            invoice: record as QboInvoiceRecord,
            formatDate,
        }));
    } else if (isApLedger && apLedgerRecord) {
        ({ title, subtitle, amountDisplay, metadataContent } = buildApLedgerDrawerPresentation({
            apLedgerRecord: effectiveApLedgerRecord || apLedgerRecord,
            formatDate,
            formatTitleCase,
            formatConfidencePercent,
            renderBankReceiptLink,
            onExpectedPaymentDateChange: (date) => updateApExpectedPaymentDateMutation.mutate(date),
            isUpdatingExpectedPaymentDate: updateApExpectedPaymentDateMutation.isPending,
        }));
    } else if (isQboBill) {
        const bill = record as QboBillRecord;
        const apBillStatus = getApBillStatus(bill);
        ({ title, subtitle, amountDisplay, metadataContent } = buildQboBillDrawerPresentation({
            bill,
            formatDate,
            apBillStatus,
        }));
    } else if (isUploaded) {
        const displayFileName = uploadedRecord?.file_original_name || uploadedRecord?.file_name || uploadedRecord?.file_id || '—';
        const canOpenDocument = !!uploadedRecord?.file_id && !!onOpenDocumentFile;
        ({ title, subtitle, amountDisplay, metadataContent } = buildUploadedDrawerPresentation({
            uploadedRecord: uploadedRecord!,
            isUploadedInvoice,
            isUploadedExpense,
            uploadedStatus,
            canOpenDocument,
            displayFileName,
            formatDate,
            getDocumentVendorName,
            onOpenDocumentFile,
        }));
    }

    return (
        <>
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} />

            <div className={`fixed inset-y-0 right-0 w-full mt-12 sm:mt-0 sm:w-[460px] max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
                <div className="p-6 border-b border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                            {isInventory ? <Package2 size={20} /> : <Hash size={20} />}
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors">
                            <X size={18} />
                        </button>
                    </div>

                    <div>
                        <h2 className="text-xl font-bold text-gray-900 leading-tight">{title}</h2>
                        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                        {amountDisplay}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* {isUploaded && uploadedRecord && (
                        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <div>
                                <p className="text-sm font-semibold text-amber-900">Uploaded document workflow</p>
                                <p className="text-sm text-amber-800 mt-1">
                                    {uploadedStatus === 'confirmed'
                                        ? 'This document has already been confirmed.'
                                        : 'This document still needs review before it is finalized.'}
                                </p>
                            </div>
                            {uploadedStatus !== 'confirmed' && onReviewDocument && (
                                <button
                                    onClick={() => onReviewDocument(uploadedRecord)}
                                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-white px-4 py-2 text-sm font-medium text-amber-900 border border-amber-300 hover:bg-amber-100"
                                >
                                    <FileSearch size={16} />
                                    Review Document
                                </button>
                            )}
                        </div>
                    )} */}

                    {metadataContent}

                    {isUploaded && uploadedRecord && !isUploadedExpense && (
                        <UploadedPriceSignalsSection uploadedRecord={uploadedRecord} />
                    )}

                    <AccountingLineItemsSection
                        lineItems={lineItems}
                        currency={(!isInventory && !isTransactionList)
                            ? ((record as QboInvoiceRecord | QboBillRecord | UploadedLedgerRecord | null)?.data?.currency || 'USD')
                            : 'USD'}
                        getLineItemQuantity={getLineItemQuantity}
                    />

                    {/* {isUploaded && uploadedRecord?.extracted_text && (
                        <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Extracted Text</h3>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs leading-6 text-gray-600 max-h-48 overflow-y-auto">
                                {uploadedRecord.extracted_text}
                            </div>
                        </div>
                    )} */}

                    {/* {isUploaded && uploadedStatus === 'confirmed' && (
                        <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-start gap-3">
                            <CheckCircle2 className="text-green-600 mt-0.5" size={18} />
                            <div>
                                <p className="text-sm font-medium text-green-900">Confirmed document</p>
                                <p className="text-sm text-green-800 mt-1">
                                    This uploaded document is already confirmed and reflected in your document workflow.
                                </p>
                            </div>
                        </div>
                    )} */}
                </div>
            </div>

            {apRecurringForm && (
                <>
                    <div
                        className="fixed inset-0 z-[60] bg-slate-900/30"
                        onClick={() => {
                            if (!upsertApRecurringOverrideMutation.isPending) {
                                setApRecurringForm(null);
                                setIsRecurringAmountSuggestionsOpen(false);
                                setIsRecurringDatePickerOpen(false);
                            }
                        }}
                    />
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                        <div className="w-full max-w-xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    {apRecurringForm.mode === 'edit' ? 'Edit recurring' : 'Add recurring'}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Update recurring settings and use a calendar picker for the next expected date.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                                onClick={() => setApRecurringForm(null)}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="space-y-4 px-5 py-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <label className="space-y-1">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Vendor</span>
                                    <input
                                        type="text"
                                        value={apRecurringForm.vendorName}
                                        onChange={(event) => setApRecurringForm((current) => current ? { ...current, vendorName: event.target.value } : current)}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    />
                                </label>
                                <label className="space-y-1">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Frequency</span>
                                    <select
                                        value={apRecurringForm.frequency}
                                        onChange={(event) => setApRecurringForm((current) => current ? { ...current, frequency: event.target.value } : current)}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    >
                                        <option value="weekly">Weekly</option>
                                        <option value="biweekly">Biweekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="annually">Annually</option>
                                    </select>
                                </label>
                                <label className="space-y-1 md:col-span-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Account name</span>
                                    <button
                                        type="button"
                                        onClick={() => setIsRecurringAccountSelectorOpen(true)}
                                        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 transition-colors hover:border-indigo-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    >
                                        <span>{apRecurringForm.accountingAccountName || 'Select account'}</span>
                                        <span className="text-xs text-gray-400">Choose</span>
                                    </button>
                                </label>
                                {/* <label className="space-y-1">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Category</span>
                                    <input
                                        type="text"
                                        value={apRecurringForm.accountingCategory}
                                        readOnly
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    />
                                </label> */}
                                <label className="space-y-1">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Typical amount</span>
                                    <div className="relative space-y-2">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={apRecurringForm.typicalAmount}
                                            onChange={(event) => setApRecurringForm((current) => current ? { ...current, typicalAmount: event.target.value } : current)}
                                            onFocus={() => {
                                                if ((apRecurringForm.suggestedAmounts || []).length > 0) {
                                                    setIsRecurringAmountSuggestionsOpen(true);
                                                }
                                            }}
                                            onClick={() => {
                                                if ((apRecurringForm.suggestedAmounts || []).length > 0) {
                                                    setIsRecurringAmountSuggestionsOpen(true);
                                                }
                                            }}
                                            onBlur={() => {
                                                window.setTimeout(() => setIsRecurringAmountSuggestionsOpen(false), 120);
                                            }}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                            placeholder={(apRecurringForm.suggestedAmounts || []).length > 0 ? 'Type or choose a past amount' : undefined}
                                        />
                                        {isRecurringAmountSuggestionsOpen && (apRecurringForm.suggestedAmounts || []).length > 0 && (
                                            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                                                {(apRecurringForm.suggestedAmounts || []).map((amount) => (
                                                    <button
                                                        key={amount}
                                                        type="button"
                                                        onMouseDown={(event) => {
                                                            event.preventDefault();
                                                            setApRecurringForm((current) => current ? { ...current, typicalAmount: String(amount) } : current);
                                                            setIsRecurringAmountSuggestionsOpen(false);
                                                        }}
                                                        className="block w-full px-3 py-2 text-left text-sm text-gray-900 transition-colors hover:bg-slate-50"
                                                    >
                                                        {formatCurrency(amount, 'USD')}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </label>
                                <div className="space-y-1">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Expected payment date</span>
                                    <div ref={recurringDatePickerRef} className="w-full">
                                        <DatePicker
                                            open={isRecurringDatePickerOpen}
                                            selected={apRecurringForm.nextExpectedPaymentDate}
                                            onChange={(date: Date | null) => {
                                                setApRecurringForm((current) => current ? { ...current, nextExpectedPaymentDate: date } : current);
                                                setIsRecurringDatePickerOpen(false);
                                            }}
                                            onClickOutside={() => setIsRecurringDatePickerOpen(false)}
                                            onInputClick={() => setIsRecurringDatePickerOpen(true)}
                                            onSelect={() => setIsRecurringDatePickerOpen(false)}
                                            dateFormat="yyyy-MM-dd"
                                            wrapperClassName="w-full"
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                            placeholderText="Select a date"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
                            <button
                                type="button"
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                onClick={() => setApRecurringForm(null)}
                                disabled={upsertApRecurringOverrideMutation.isPending}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={submitApRecurringForm}
                                disabled={upsertApRecurringOverrideMutation.isPending}
                            >
                                {upsertApRecurringOverrideMutation.isPending ? 'Saving...' : apRecurringForm.mode === 'edit' ? 'Save changes' : 'Add recurring'}
                            </button>
                        </div>
                    </div>
                    </div>
                </>
            )}

            <StandardAccountSelector
                isOpen={isVendorAccountSelectorOpen}
                onClose={() => setIsVendorAccountSelectorOpen(false)}
                businessId={businessId}
                currentAccountId={
                    apVendorDetail?.resolved_primary_accounting_account_id
                    || apVendorRecord?.resolved_primary_accounting_account_id
                    || apVendorRecord?.primary_accounting_account_id
                    || apVendorDetail?.profile?.primary_accounting_account_id
                    || undefined
                }
                includeInternalTransfer={false}
                onSelect={(account) => updateApVendorAccountMutation.mutate(account)}
            />

            <StandardAccountSelector
                isOpen={isRecurringAccountSelectorOpen}
                onClose={() => setIsRecurringAccountSelectorOpen(false)}
                businessId={businessId}
                onSelect={handleSelectRecurringAccount}
            />

            {isArLedger && arLedgerRecord && showArManualLinkPicker && (
                <>
                    <div
                        className="fixed inset-0 z-[60] bg-slate-900/30"
                        onClick={() => {
                            if (!applyArManualLinkMutation.isPending && !applyArManualAllocationMutation.isPending) {
                                setShowArManualLinkPicker(false);
                            }
                        }}
                    />
                    <div className="fixed inset-x-4 top-20 z-[70] mx-auto w-full max-w-5xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">{isArPaymentRecord ? 'AR Bank Evidence' : 'AR Payment Allocation'}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {isArPaymentRecord
                                        ? 'Confirm the bank evidence for this AR row. If this is a payment, you can also decide how the cash should be applied to invoices.'
                                        : 'Find one or more payments for this invoice, then open each payment and allocate part of the cash to this invoice.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                                onClick={() => setShowArManualLinkPicker(false)}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="max-h-[74vh] overflow-hidden px-5 py-4 space-y-4">
                            <div className="grid gap-3 md:grid-cols-1">
                                <input
                                    type="text"
                                    value={arManualSearch}
                                    onChange={(event) => setArManualSearch(event.target.value)}
                                    placeholder={isArPaymentRecord ? 'Search bank counterparty, memo, amount...' : 'Search payment id, customer, invoice number, amount...'}
                                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                />
                            </div>

                            {hasArManualOverride && (
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-900">
                                    <div className="font-medium text-indigo-900">Current manual override</div>
                                    <div className="mt-1">
                                        Bank: {String(arManualOverrideBankSummary?.counterparty_name || arManualOverrideBankSummary?.memo || '—')}
                                        {arManualOverrideBankSummary?.transaction_date ? ` · ${formatDate(arManualOverrideBankSummary.transaction_date)}` : ''}
                                        {typeof arManualOverrideBankSummary?.amount === 'number' ? ` · ${formatCurrency(arManualOverrideBankSummary.amount, 'USD')}` : ''}
                                    </div>
                                </div>
                            )}

                            {(allocationLinkedPayments.length > 0 || allocationLinkedBankReceipts.length > 0) && (
                                <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700">
                                    <div className="font-medium text-slate-900">Current linked evidence</div>
                                    <div className="mt-1">
                                        Bank: {allocationLinkedBankReceipts.length
                                            ? allocationLinkedBankReceipts.map((receipt: ArLedgerDetailBankReceipt) => getFriendlyBankReceiptLabel(receipt, arLedgerRecord?.customer_name)).join(', ')
                                            : 'none'}
                                    </div>
                                </div>
                            )}

                            <div className={`grid gap-3 ${isArPaymentRecord ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                                <button
                                    type="button"
                                    onClick={() => setArManualMatchSection('bank')}
                                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${arManualMatchSection === 'bank' ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                                >
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{isArPaymentRecord ? 'Selected Bank Evidence' : 'Available Payments'}</p>
                                    <p className="mt-1 text-sm font-medium text-slate-900">
                                        {isArPaymentRecord
                                            ? (selectedArManualBankCandidate ? getArManualCandidateTitle(selectedArManualBankCandidate) : 'Choose bank evidence')
                                            : (arInvoicePaymentCandidates.length > 0 ? `${arInvoicePaymentCandidates.length} payment candidate${arInvoicePaymentCandidates.length === 1 ? '' : 's'}` : 'No payment candidates found')}
                                    </p>
                                </button>
                                {isArPaymentRecord && (
                                    <button
                                        type="button"
                                        onClick={() => setArManualMatchSection('allocation')}
                                        className={`rounded-xl border px-4 py-3 text-left transition-colors ${arManualMatchSection === 'allocation' ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                                    >
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Apply To Invoices</p>
                                        <p className="mt-1 text-sm font-medium text-slate-900">
                                            {allocationPreviewItems.length > 0
                                                ? (allocationRemaining > 0 ? 'Partial allocation draft' : 'Full allocation draft')
                                                : 'No allocation yet'}
                                        </p>
                                    </button>
                                )}
                            </div>

                            <div className="space-y-4">
                                {arManualMatchSection === 'bank' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{isArPaymentRecord ? 'Choose Bank Evidence' : 'Available Payments'}</h4>
                                        {!isArPaymentRecord && (
                                            <>
                                                <div className="grid gap-3 md:grid-cols-4">
                                                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Total</p>
                                                        <p className="mt-1 text-lg font-semibold text-slate-900">{formatCurrency(arLedgerRecord?.amount, 'USD')}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Open Now</p>
                                                        <p className="mt-1 text-lg font-semibold text-amber-900">{formatCurrency(invoiceCurrentOpenAmount, 'USD')}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Draft Apply</p>
                                                        <p className="mt-1 text-lg font-semibold text-emerald-900">{formatCurrency(invoiceDraftAppliedTotal, 'USD')}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Remaining After Save</p>
                                                        <p className="mt-1 text-lg font-semibold text-indigo-900">{formatCurrency(invoiceProjectedOpenAmount, 'USD')}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2">
                                                    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                                                        <button
                                                            type="button"
                                                            className={`rounded-md px-3 py-1.5 text-xs font-medium ${invoicePaymentScope === 'customer' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                                            onClick={() => setInvoicePaymentScope('customer')}
                                                        >
                                                            Same customer
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`rounded-md px-3 py-1.5 text-xs font-medium ${invoicePaymentScope === 'all' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                                            onClick={() => setInvoicePaymentScope('all')}
                                                        >
                                                            All payments
                                                        </button>
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {invoicePaymentScope === 'all' ? 'Search across all payment rows, including already allocated ones.' : 'Showing payments for the current customer first.'}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-gray-200">
                                            {isArManualLinkCandidatesLoading ? (
                                                <div className="px-4 py-4 text-sm text-gray-500 flex items-center gap-2">
                                                    <Loader2 size={14} className="animate-spin" />
                                                    Loading candidates...
                                                </div>
                                            ) : !isArPaymentRecord && arInvoicePaymentCandidates.length === 0 ? (
                                                <div className="px-4 py-4 text-sm text-gray-500">No payment candidates found for this invoice yet.</div>
                                            ) : isArPaymentRecord && arManualBankCandidates.length === 0 ? (
                                                <div className="px-4 py-4 text-sm text-gray-500">No bank candidates found.</div>
                                            ) : (
                                                <div className="divide-y divide-gray-100">
                                                    {isArPaymentRecord ? arManualBankCandidates.map((candidate) => (
                                                        <button
                                                            key={`ar-bank-${candidate.id}`}
                                                            type="button"
                                                            className={`w-full px-4 py-3 text-left text-sm ${selectedArManualBankId === candidate.id ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'}`}
                                                            onClick={() => {
                                                                setSelectedArManualBankId((current) => current === candidate.id ? '' : candidate.id);
                                                                if (selectedArManualBankId !== candidate.id && isArPaymentRecord) {
                                                                    setArManualMatchSection('allocation');
                                                                }
                                                            }}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-gray-900">{getArManualCandidateTitle(candidate)}</p>
                                                                    <p className="mt-1 text-xs text-indigo-700">{getArManualCandidateTypeLabel(candidate)}</p>
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {isLowConfidenceGenericArBankCandidate(candidate) && (
                                                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                                Low confidence
                                                                            </span>
                                                                        )}
                                                                        {candidate.is_generic_counterparty && (
                                                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                                                Generic counterparty
                                                                            </span>
                                                                        )}
                                                                        {candidate.status && (
                                                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                                                {formatTitleCase(candidate.status)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {isLowConfidenceGenericArBankCandidate(candidate) && (
                                                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                                Low confidence
                                                                            </span>
                                                                        )}
                                                                        {candidate.is_generic_counterparty && (
                                                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                                                Generic counterparty
                                                                            </span>
                                                                        )}
                                                                        {candidate.status && (
                                                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                                                {formatTitleCase(candidate.status)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {!!getArManualCandidateDeltaLabel(candidate) && (
                                                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 whitespace-nowrap">
                                                                        {getArManualCandidateDeltaLabel(candidate)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {selectedArManualBankId === candidate.id && (
                                                                <p className="mt-2 text-[11px] font-medium text-indigo-700">Selected. Click again to unselect.</p>
                                                            )}
                                                            <p className="mt-1 text-xs text-gray-500">
                                                                {formatDate(candidate.transaction_date || undefined)} · {formatCurrency(candidate.amount, candidate.currency || 'USD')}
                                                                {candidate.account_name ? ` · ${candidate.account_name}` : ''}
                                                            </p>
                                                            {!!getArManualBankCandidateGuidance(candidate) && (
                                                                <p className="mt-2 text-xs text-amber-700">
                                                                    {getArManualBankCandidateGuidance(candidate)}
                                                                </p>
                                                            )}
                                                            {selectedArManualBankId === candidate.id && isLowConfidenceGenericArBankCandidate(candidate) && (
                                                                <p className="mt-3 text-xs font-medium text-amber-700">
                                                                    Selected. Use <span className="font-semibold">Confirm Generic Evidence</span> below to attach this bank proof.
                                                                </p>
                                                            )}
                                                            {!!getArManualBankCandidateGuidance(candidate) && (
                                                                <p className="mt-2 text-xs text-amber-700">
                                                                    {getArManualBankCandidateGuidance(candidate)}
                                                                </p>
                                                            )}
                                                            {selectedArManualBankId === candidate.id && isLowConfidenceGenericArBankCandidate(candidate) && (
                                                                <p className="mt-3 text-xs font-medium text-amber-700">
                                                                    Selected. Use <span className="font-semibold">Confirm Generic Evidence</span> below to attach this bank proof.
                                                                </p>
                                                            )}
                                                        </button>
                                                    )) : arInvoicePaymentCandidates.map((candidate) => {
                                                        const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
                                                        const remainingCash = clampMoney(Number(candidate.open_amount ?? candidate.amount ?? 0));
                                                        const existingAllocatedAmount = invoiceExistingAllocationByPaymentSourceId.get(paymentSourceId) || 0;
                                                        const draftValue = invoicePaymentDraft[paymentSourceId] || '';
                                                        const currentDraftAmount = clampMoney(Number(draftValue || 0));
                                                        const editableMax = clampMoney(remainingCash + existingAllocatedAmount);
                                                        return (
                                                            <div key={`ar-payment-${paymentSourceId}`} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-gray-900">
                                                                        {renderPaymentLink(paymentSourceId, candidate.document_number || candidate.customer_name || candidate.counterparty_name || paymentSourceId)}
                                                                    </p>
                                                                    <p className="mt-1 text-xs text-indigo-700">
                                                                        {(candidate.payment_evidence_sources || []).length > 0
                                                                            ? (candidate.payment_evidence_sources || []).map((source) => formatTitleCase(source)).join(' + ')
                                                                            : 'Payment candidate'}
                                                                    </p>
                                                                    <p className="mt-1 text-xs text-gray-500">
                                                                        {[
                                                                            formatDate(candidate.transaction_date || undefined),
                                                                            candidate.customer_name || candidate.counterparty_name,
                                                                            candidate.document_number ? `Ref ${candidate.document_number}` : '',
                                                                        ].filter(Boolean).join(' · ') || 'No extra details'}
                                                                    </p>
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {candidate.status && (
                                                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                                                {formatTitleCase(candidate.status)}
                                                                            </span>
                                                                        )}
                                                                        {typeof candidate.open_amount === 'number' && (
                                                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                                Remaining {formatCurrency(candidate.open_amount, candidate.currency || 'USD')}
                                                                            </span>
                                                                        )}
                                                                        {currentDraftAmount > 0 && (
                                                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                                                                                Applying {formatCurrency(currentDraftAmount, candidate.currency || 'USD')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex shrink-0 items-center gap-3">
                                                                    <div className="text-right">
                                                                        <div className="font-semibold text-gray-900">{formatCurrency(candidate.amount, candidate.currency || 'USD')}</div>
                                                                        {!!getArManualCandidateDeltaLabel(candidate) && (
                                                                            <div className="mt-1 text-xs text-gray-500">
                                                                                {getArManualCandidateDeltaLabel(candidate)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="w-24">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={draftValue}
                                                                            onChange={(event) => {
                                                                                const nextRaw = event.target.value;
                                                                                if (!paymentSourceId) return;
                                                                                if (nextRaw === '') {
                                                                                    setInvoicePaymentDraft((current) => ({ ...current, [paymentSourceId]: '' }));
                                                                                    return;
                                                                                }
                                                                                const parsed = Number(nextRaw);
                                                                                if (!Number.isFinite(parsed)) {
                                                                                    setInvoicePaymentDraft((current) => ({ ...current, [paymentSourceId]: nextRaw }));
                                                                                    return;
                                                                                }
                                                                                if (parsed < 0) {
                                                                                    setInvoicePaymentDraft((current) => ({ ...current, [paymentSourceId]: '' }));
                                                                                    return;
                                                                                }
                                                                                if (parsed > editableMax) {
                                                                                    setInvoicePaymentDraft((current) => ({ ...current, [paymentSourceId]: editableMax > 0 ? editableMax.toFixed(2) : '' }));
                                                                                    return;
                                                                                }
                                                                                setInvoicePaymentDraft((current) => ({ ...current, [paymentSourceId]: nextRaw }));
                                                                            }}
                                                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-right text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {isArPaymentRecord && arManualMatchSection === 'allocation' && (
                                    <>
                                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment Total</p>
                                                <p className="mt-1 text-lg font-semibold text-slate-900">{formatCurrency(allocationPaymentTotal, 'USD')}</p>
                                            </div>
                                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Allocated</p>
                                                <p className="mt-1 text-lg font-semibold text-emerald-900">{formatCurrency(allocationDraftTotal, 'USD')}</p>
                                            </div>
                                            <div className={`rounded-xl border px-4 py-3 ${allocationRemaining > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                                                <p className={`text-xs font-semibold uppercase tracking-wide ${allocationRemaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>Remaining Cash</p>
                                                <p className={`mt-1 text-lg font-semibold ${allocationRemaining > 0 ? 'text-amber-900' : 'text-emerald-900'}`}>{formatCurrency(allocationRemaining, 'USD')}</p>
                                            </div>
                                            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                                                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Evidence Status</p>
                                                <p className="mt-1 text-sm font-semibold text-indigo-900">{allocationHasEvidence || !!selectedArManualBankId ? 'Matched and ready for allocation' : 'Missing bank evidence'}</p>
                                                <p className="mt-1 text-sm font-semibold text-indigo-900">{allocationHasEvidence || !!selectedArManualBankId ? 'Matched and ready for allocation' : 'Missing bank evidence'}</p>
                                            </div>
                                        </div>

                                        <div className="max-h-[44vh] overflow-y-auto rounded-xl border border-gray-200">
                                            <div className="border-b border-gray-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                Apply To Invoice(s)
                                            </div>
                                            <div className="grid gap-3 border-b border-gray-200 bg-white px-4 py-3 md:grid-cols-[auto_auto_minmax(0,1fr)]">
                                                <div className="inline-flex rounded-lg border border-gray-200 bg-slate-50 p-1">
                                                    <button
                                                        type="button"
                                                        className={`rounded-md px-3 py-1.5 text-xs font-medium ${allocationInvoiceScope === 'customer' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                                        onClick={() => setAllocationInvoiceScope('customer')}
                                                    >
                                                        Same customer
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`rounded-md px-3 py-1.5 text-xs font-medium ${allocationInvoiceScope === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                                        onClick={() => setAllocationInvoiceScope('all')}
                                                    >
                                                        All invoices
                                                    </button>
                                                </div>
                                                <div className="self-center text-xs text-slate-500">
                                                    {allocationInvoiceScope === 'all' ? 'Cross-customer search enabled, including already allocated invoices.' : 'Showing invoices for the current customer first.'}
                                                </div>
                                                <input
                                                    type="text"
                                                    value={allocationInvoiceSearch}
                                                    onChange={(event) => setAllocationInvoiceSearch(event.target.value)}
                                                    placeholder="Search invoice number or customer..."
                                                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                                                />
                                            </div>
                                            <div className="grid grid-cols-[minmax(0,1.5fr)_120px_120px] gap-3 px-4 py-3 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                <span>Invoice</span>
                                                <span className="text-right">Open</span>
                                                <span className="text-right">Apply</span>
                                            </div>
                                            {isArOpenInvoicesLoading ? (
                                                <div className="px-4 py-4 text-sm text-gray-500 flex items-center gap-2">
                                                    <Loader2 size={14} className="animate-spin" />
                                                    Loading invoice candidates...
                                                </div>
                                            ) : allocationInvoices.length === 0 ? (
                                                <div className="px-4 py-4 text-sm text-gray-500">
                                                    {allocationInvoiceScope === 'all'
                                                        ? 'No invoices match this search yet.'
                                                        : 'No invoice candidates are available for this customer yet.'}
                                                </div>
                                            ) : (
                                                <div className="divide-y divide-gray-100">
                                                    {allocationInvoices.map((invoice, index) => {
                                                        const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                                                        const openAmount = clampMoney(Number(invoice.open_amount_finz ?? invoice.open_amount ?? invoice.balance_due_qbo ?? 0));
                                                        const draftValue = allocationDraft[invoiceId] || '';
                                                        return (
                                                            <div key={invoiceId || `allocation-invoice-${index}`} className="grid grid-cols-[minmax(0,1.5fr)_120px_120px] gap-3 px-4 py-3 items-center">
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {renderArInvoiceLink(invoice.invoice_id || invoice.source_id, invoice.invoice_number, invoice.invoice_number || 'Invoice')}
                                                                    </p>
                                                                    <p className="mt-1 text-xs text-gray-500">
                                                                        Due {formatDate(invoice.due_date || undefined)} · {invoice.customer_name || arLedgerRecord?.customer_name || '—'}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right text-sm font-medium text-gray-900 whitespace-nowrap">
                                                                    {formatCurrency(openAmount, 'USD')}
                                                                </div>
                                                                <div>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={draftValue}
                                                                        onChange={(event) => {
                                                                            const nextRaw = event.target.value;
                                                                            if (!invoiceId) return;
                                                                            if (nextRaw === '') {
                                                                                setAllocationDraft((current) => ({ ...current, [invoiceId]: '' }));
                                                                                return;
                                                                            }
                                                                            const parsed = Number(nextRaw);
                                                                            if (!Number.isFinite(parsed)) {
                                                                                setAllocationDraft((current) => ({ ...current, [invoiceId]: nextRaw }));
                                                                                return;
                                                                            }
                                                                            if (parsed < 0) {
                                                                                setAllocationDraft((current) => ({ ...current, [invoiceId]: '' }));
                                                                                return;
                                                                            }
                                                                            if (parsed > openAmount) {
                                                                                setAllocationDraft((current) => ({ ...current, [invoiceId]: openAmount > 0 ? openAmount.toFixed(2) : '' }));
                                                                                return;
                                                                            }
                                                                            setAllocationDraft((current) => ({ ...current, [invoiceId]: nextRaw }));
                                                                        }}
                                                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-right text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                            <p className="text-xs text-gray-500">
                                {isArPaymentRecord
                                    ? 'Confirm the bank evidence first. Save allocation only when you are ready to change invoice balances.'
                                    : 'Allocate one or more payments directly to this invoice. Existing allocations on each payment will be preserved.'}
                            </p>
                            <div className="flex items-center gap-2">
                                {hasArManualOverride && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-md border border-rose-300 px-4 py-2 text-sm text-rose-700 hover:bg-rose-50 whitespace-nowrap"
                                        onClick={() => removeArManualLinkMutation.mutate()}
                                        disabled={applyArManualLinkMutation.isPending || applyArManualAllocationMutation.isPending || applyArManualInvoiceAllocationMutation.isPending || removeArManualLinkMutation.isPending}
                                    >
                                        {removeArManualLinkMutation.isPending ? 'Removing...' : 'Remove Manual Match'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                                    onClick={() => setShowArManualLinkPicker(false)}
                                    disabled={applyArManualLinkMutation.isPending || applyArManualAllocationMutation.isPending || applyArManualInvoiceAllocationMutation.isPending || removeArManualLinkMutation.isPending}
                                >
                                    Cancel
                                </button>
                                {isArPaymentRecord && arManualMatchSection === 'allocation' && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                                        onClick={() => {
                                            const nextDraft: Record<string, string> = {};
                                            let remaining = allocationPaymentTotal;
                                            allocationInvoices.forEach((invoice) => {
                                                const invoiceId = String(invoice.invoice_id || invoice.source_id || '').trim();
                                                if (!invoiceId || remaining <= 0) {
                                                    nextDraft[invoiceId] = '';
                                                    return;
                                                }
                                                const openAmount = clampMoney(Number(invoice.open_amount_finz ?? invoice.open_amount ?? invoice.balance_due_qbo ?? 0));
                                                const amountToApply = clampMoney(Math.min(openAmount, remaining));
                                                nextDraft[invoiceId] = amountToApply > 0 ? amountToApply.toFixed(2) : '';
                                                remaining = clampMoney(remaining - amountToApply);
                                            });
                                            setAllocationDraft(nextDraft);
                                        }}
                                        disabled={applyArManualAllocationMutation.isPending}
                                    >
                                        Auto Fill
                                    </button>
                                )}
                                {isArPaymentRecord && arManualMatchSection === 'bank' && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60 whitespace-nowrap"
                                        disabled={applyArManualLinkMutation.isPending || applyArManualAllocationMutation.isPending || removeArManualLinkMutation.isPending || !selectedArManualBankId}
                                        onClick={() => applyArManualLinkMutation.mutate()}
                                    >
                                        {applyArManualLinkMutation.isPending ? 'Saving...' : selectedArManualBankIsLowConfidenceGeneric ? 'Confirm Generic Evidence' : 'Save Evidence'}
                                    </button>
                                )}
                                {isArPaymentRecord && arManualMatchSection === 'allocation' && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap"
                                        disabled={
                                            applyArManualAllocationMutation.isPending
                                            || allocationDraftTotal <= 0
                                            || allocationDraftTotal - allocationPaymentTotal > 0.01
                                            || (!allocationHasEvidence && !selectedArManualBankId)
                                        }
                                        onClick={() => applyArManualAllocationMutation.mutate()}
                                    >
                                        {applyArManualAllocationMutation.isPending ? 'Saving...' : 'Save Allocation'}
                                    </button>
                                )}
                                {!isArPaymentRecord && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap"
                                        disabled={
                                            applyArManualInvoiceAllocationMutation.isPending
                                            || arInvoicePaymentCandidates.every((candidate) => {
                                                const paymentSourceId = String(candidate.source_id || candidate.id || '').trim();
                                                return clampMoney(Number(invoicePaymentDraft[paymentSourceId] || 0)) <= 0;
                                            })
                                        }
                                        onClick={() => applyArManualInvoiceAllocationMutation.mutate()}
                                    >
                                        {applyArManualInvoiceAllocationMutation.isPending ? 'Saving...' : 'Save Allocation'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

        </>
    );
};

export default LedgerRecordDrawer;
